import { Controller } from '@hotwired/stimulus'
let intlTelInputLib = null

export default class PhoneNumberController extends Controller {
  async initialize() {
    await this.loadLib()
    this.setupPhoneInput()
  }

  async loadLib() {
    if (intlTelInputLib) return

    const module = await import('intl-tel-input/intlTelInputWithUtils')
    intlTelInputLib = module.default || module
  }

  setupPhoneInput() {
    const intlTelInput = intlTelInputLib
    this.handleInput = (event) => {
      event.target.value = event.target.value.replaceAll(/[a-zA-Z]/g, '')
      this.applyPhoneMask(event)
    }

    let dialCode = this.element.dataset.countryCode
    dialCode = dialCode || 'us'
    let countryCode = dialCode
    const countryData = intlTelInput.getCountryData()
    for (const country of countryData) {
      if (country.iso2 === dialCode) {
        countryCode = country.iso2
        break
      }
    }

    const errorMap = [
      'Invalid number',
      'Invalid country code',
      'Phone number is too short',
      'Phone number is too long',
      'Invalid number'
    ]
    this.errorMap = errorMap

    const iti = intlTelInput(this.element, {
      initialCountry: countryCode,
      separateDialCode: true,
      utilsScript: intlTelInput.utils,
      formatOnDisplay: true,
      countrySearch: true,
      // The mobile fullscreen picker is detached from the input. Keep it inside
      // the Bootstrap modal so the modal focus trap does not steal focus from
      // the country search input.
      dropdownContainer: this.element.closest('.modal'),
      autoInsertDialCode: false,
      nationalMode: false
    })
    this.iti = iti

    this.currentCountry = countryCode;
    this.element.addEventListener("countrychange", () => {
      const selectedCountry = this.iti.getSelectedCountryData().iso2;

      if (this.userSelectingCountry) {
        this.currentCountry = selectedCountry;
        this.userSelectingCountry = false;

        document.querySelector(".country_code").value = selectedCountry;
        this.setPlaceholder(selectedCountry);
        return;
      }

      requestAnimationFrame(() => {
        this.iti.setCountry(this.currentCountry);

        document.querySelector(".country_code").value = this.currentCountry;
        this.setPlaceholder(this.currentCountry);

        if (this.element.value.trim()) {
          this.applyPhoneMask({ target: this.element });
        }
      });
    });

    this.element.addEventListener("open:countrydropdown", () => {
      this.userSelectingCountry = true;
    });

    this.element.addEventListener('input', this.handleInput)
    this.element.addEventListener(
      'blur',
      function () {
        this.reset(iti)
        if (this.element.value) {
          if (iti.isValidNumber()) {
            this.element
              .closest('form')
              .querySelector('button[type="submit"]').disabled = false
          } else {
            this.element.classList.add('error')
            const errorCode = iti.getValidationError()            
            const errorMessage = errorMap[errorCode] || 'Invalid number'
            const errorContainer = this.element.closest('.form-group').querySelector('.error-msg')
            if (errorContainer) {
              errorContainer.textContent = errorMessage
            }
            this.element
              .closest('form')
              .querySelector('button[type="submit"]').disabled = true
          }
        }
      }.bind(this)
    )

    // Reformat only after an edit has finished. Re-parsing and setting the number on
    // every keyup causes noticeable input lag in Chrome.
    this.element.addEventListener('change', () => this.reset(iti))
  }

  connect() {
    this.setPlaceholder(this.element.dataset.countryCode || "us");
    this.setupFormValidation()
  }

  reset(iti) {
    this.element.classList.remove('error')

    if (typeof intlTelInputUtils !== 'undefined') {
      const currentFormat = iti.getNumber(
        intlTelInputUtils.numberFormat.INTERNATIONAL
      )
      if (typeof currentFormat === 'string') {
        iti.setNumber(currentFormat)
      }
    }
    this.applyPhoneMask({ target: this.element })
  }

  validateInput() {
    this.resetErrors()

    if (this.element.disabled) return
    if (!this.element.value.trim()) return

    if (this.iti.isValidNumber()) {
      this.enableSubmit()
    } else {
      this.showError(this.errorMap[this.iti.getValidationError()])
      this.disableSubmit()
    }
  }

  enableSubmit() {
    const btn = this.element
      .closest('form')
      .querySelector('button[type="submit"]')
    if (btn) btn.disabled = false
  }

  disableSubmit() {
    const btn = this.element
      .closest('form')
      .querySelector('button[type="submit"]')
    if (btn) btn.disabled = true
  }

  setupFormValidation() {
    const form = this.element.closest('form')

    form.addEventListener('change', (e) => {
      if (this.element.disabled) return

      if (!this.iti.isValidNumber()) {
        e.preventDefault()
        e.stopImmediatePropagation()
        this.validateInput()
        this.applyPhoneMask({ target: this.element })
      }
    })
  }

  showError(msg) {
    this.element.classList.add('error')
    const errorContainer = this.element
      .closest('.form-group')
      .querySelector('.error-msg')
    if (errorContainer) errorContainer.textContent = msg
  }

  resetErrors() {
    this.element.classList.remove('error')
    const errorMsg = this.element
      .closest('.form-group')
      .querySelector('.error-msg')
    if (errorMsg) errorMsg.textContent = ''

    if (typeof intlTelInputUtils !== 'undefined') {
      const formatted = this.iti.getNumber(
        intlTelInputUtils.numberFormat.INTERNATIONAL
      )
      if (formatted) this.iti.setNumber(formatted)
    }
  }

  applyPhoneMask(event) {
    const el = event.target
    const mask = el.placeholder || ''
    const digits = el.value.replaceAll(/\D/g, '')

    if (!digits) {
      el.value = ''
      return
    }

    let result = ''
    let digitIndex = 0
    for (const ch of mask) {
      if (digitIndex >= digits.length) break

      if (ch === 'X') {
        result += digits[digitIndex++]
        continue
      }

      result += ch
    }

    // Do not discard extra digits. intl-tel-input needs to receive the complete
    // value in order to return its "Phone number is too long" validation error.
    if (digitIndex < digits.length) result += digits.slice(digitIndex)

    const newCursorPos = this.setCursorPosition(
      el.selectionStart,
      el.value,
      result
    )
    el.value = result
    el.setSelectionRange(newCursorPos, newCursorPos)
  }

  setCursorPosition(selectionStart, oldValue, newValue) {
    let newCursorPos = 0
    let digitsCounted = 0
    const digitsBeforeCursor = oldValue
      .slice(0, selectionStart)
      .replaceAll(/\D/g, '').length

    for (
      let i = 0;
      i < newValue.length && digitsCounted < digitsBeforeCursor;
      i++
    ) {
      newCursorPos++
      if (/\d/.test(newValue[i])) {
        digitsCounted++
      }
    }
    return newCursorPos
  }

  setPlaceholder(countryCode) {
    if (!intlTelInputLib) return

    const utils = intlTelInputLib.utils

    const exampleNumber = utils?.getExampleNumber?.(
      countryCode?.toUpperCase?.(),
      true,
      utils?.numberFormat?.NATIONAL
    )

    if (!exampleNumber) return

    this.element.placeholder = exampleNumber.replaceAll(/\d/g, 'X')

    if ($('#user_phone_no').html() === '"undefined".') {
      $('#user_phone_no').html(`"${exampleNumber}".`)
    }
  }
}
